import React, { useState, useEffect } from 'react';
import { Menu, X, Home, Info, Building2, Image, Clock, Sparkles, Phone, Users, Mic } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import img1 from '../Assests/logonew_mobile.png';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [isScrolled, setIsScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      // Check if page is scrolled for header styling
      setIsScrolled(window.scrollY > 50);

      // Only detect sections if we're on the home page
      if (location.pathname === '/') {
        const sections = ['home', 'about', 'video', 'departments', 'gallery', 'schedule', 'contact'];
        const scrollPosition = window.scrollY + 100;

        for (const section of sections) {
          const element = document.getElementById(section);
          if (element) {
            const offsetTop = element.offsetTop;
            const offsetBottom = offsetTop + element.offsetHeight;
            
            if (scrollPosition >= offsetTop && scrollPosition < offsetBottom) {
              setActiveSection(section);
              break;
            }
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    
    // Set active section based on current route
    if (location.pathname === '/team') {
      setActiveSection('team');
    } else if (location.pathname === '/thematic-areas') {
      setActiveSection('thematic-areas');
    } else if (location.pathname === '/speakers') {
      setActiveSection('speakers');
    } else if (location.pathname === '/') {
      // Set active section based on current scroll position
      handleScroll();
    }
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, [location.pathname]);

  const navigationItems = [
    { id: 'home', label: 'Home', icon: Home, action: () => scrollToSection('home') },
    { id: 'about', label: 'About', icon: Info, action: () => scrollToSection('about') },
    { id: 'video', label: 'Video', icon: Sparkles, action: () => scrollToSection('video') },
    { id: 'departments', label: 'Departments', icon: Building2, action: () => scrollToSection('departments') },
    { id: 'gallery', label: 'Gallery', icon: Image, action: () => scrollToSection('gallery') },
    { id: 'schedule', label: 'Schedule', icon: Clock, action: () => scrollToSection('schedule') },
    { id: 'team', label: 'Team', icon: Users, action: () => navigate('/team') },
    { id: 'thematic-areas', label: 'Thematic Areas', icon: Sparkles, action: () => navigate('/thematic-areas') },
    { id: 'speakers', label: 'Speakers', icon: Mic, action: () => navigate('/speakers') },
    { id: 'contact', label: 'Contact', icon: Phone, action: () => scrollToSection('contact') },
  ];

  const scrollToSection = (sectionId) => {
    // Set active section immediately for instant visual feedback
    setActiveSection(sectionId);
    
    // Close mobile menu first
    setIsMenuOpen(false);
    
    // If not on home page, navigate to home first then scroll to section
    if (location.pathname !== '/') {
      navigate('/');
      // Wait for navigation to complete then scroll to section
      setTimeout(() => {
        scrollToElement(sectionId);
      }, 200);
    } else {
      // Already on home page, scroll immediately
      scrollToElement(sectionId);
    }
  };

  const scrollToElement = (sectionId) => {
    // Try multiple selectors to find the section
    let element = document.getElementById(sectionId);
    
    // Special handling for contact section
    if (sectionId === 'contact') {
      // First try to find the section with id="contact"
      element = document.getElementById('contact');
      
      // If not found, try to find footer
      if (!element) {
        element = document.querySelector('footer');
      }
      
      // If still not found, try to find any element with contact in id
      if (!element) {
        element = document.querySelector('[id*="contact"]');
      }
    }
    
    if (element) {
      const headerOffset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - headerOffset;
      
      // Smooth scroll with better options
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    } else {
      console.warn(`Element with ID '${sectionId}' not found`);
      // If element not found, try again after a short delay
      setTimeout(() => {
        let retryElement = document.getElementById(sectionId);
        if (!retryElement && sectionId === 'contact') {
          retryElement = document.querySelector('footer') || document.querySelector('[id*="contact"]');
        }
        
        if (retryElement) {
          const headerOffset = 80;
          const elementPosition = retryElement.getBoundingClientRect().top + window.pageYOffset;
          const offsetPosition = elementPosition - headerOffset;
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 300);
    }
  };

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      isScrolled 
        ? 'bg-white/95 backdrop-blur-md shadow-lg' 
        : 'bg-white/90 backdrop-blur-sm shadow-md'
    }`}>
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          {/* Logo Section */}
          <div className="flex items-center space-x-4 cursor-pointer" onClick={() => {
            navigate('/');
            setActiveSection('home');
          }}>
            <div className="relative">
              <img
                src={img1}
                alt="IIT Delhi Logo"
                className="w-12 h-12 hover:scale-110 transition-transform duration-300"
              />
              {/* <div className="absolute -top-1 -right-4 w-4 h-4 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full animate-pulse"></div> */}
            </div>
            <div>
              <h1 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                IIT Delhi
              </h1>
              <p className="text-sm font-medium bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                Open House 2026
              </p>
            </div>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex space-x-1">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    item.action();
                  }}
                  className={`relative px-4 py-2 rounded-xl font-medium transition-all duration-300 flex items-center gap-2 ${
                    activeSection === item.id
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg transform scale-105'
                      : 'text-gray-600 hover:text-blue-600 hover:bg-blue-50'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{item.label}</span>
                  {activeSection === item.id && (
                    <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-white rounded-full"></div>
                  )}
                </button>
              );
            })}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden relative p-2 rounded-xl hover:bg-gray-100 transition-colors"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <div className="relative">
              {isMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </div>
          </button>
        </div>

        {/* Mobile Navigation - Fixed with proper height and scrolling */}
        {isMenuOpen && (
          <div className="lg:hidden fixed left-0 right-0 top-full bg-white/98 backdrop-blur-md shadow-2xl border-t border-gray-200 max-h-[80vh] overflow-y-auto z-40">
            <nav className="py-2 bg-white/95 backdrop-blur-md shadow-lg">
              {navigationItems.map((item, index) => {
                const Icon = item.icon;
                return (
                  <button
                    key={item.id}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      item.action();
                    }}
                    className={`flex items-center gap-4 w-full text-left px-6 py-4 transition-all duration-300 border-b border-gray-100 last:border-b-0 ${
                      activeSection === item.id
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-sm'
                        : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600 active:bg-blue-100'
                    }`}
                    style={{ 
                      animationDelay: `${index * 0.05}s`,
                      animation: isMenuOpen ? 'slideInLeft 0.3s ease-out forwards' : 'none'
                    }}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    <span className="font-medium text-base">{item.label}</span>
                    {activeSection === item.id && (
                      <div className="ml-auto w-2 h-2 bg-white rounded-full animate-pulse flex-shrink-0"></div>
                    )}
                  </button>
                );
              })}
            </nav>
          </div>
        )}

        {/* Mobile Menu Overlay - Close menu when clicking outside */}
        {isMenuOpen && (
          <div 
            className="lg:hidden fixed inset-0 bg-black/20 backdrop-blur-sm z-30"
            onClick={() => setIsMenuOpen(false)}
            style={{ top: '100%' }}
          />
        )}
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </header>
  );
};

export default Header;